import type { ComponentType } from 'react';
import { makeStyles } from '@material-ui/core';
import type { SvgIconProps } from '@material-ui/core/SvgIcon';
import CategoryIcon from '@material-ui/icons/CategoryOutlined';
import DescriptionIcon from '@material-ui/icons/DescriptionOutlined';
import DnsIcon from '@material-ui/icons/DnsOutlined';
import LibraryIcon from '@material-ui/icons/LibraryBooksOutlined';
import PeopleIcon from '@material-ui/icons/PeopleOutline';
import WebIcon from '@material-ui/icons/WebOutlined';
import { RELATION_OWNED_BY, type Entity } from '@backstage/catalog-model';
import { Link } from '@backstage/core-components';
import {
  EntityRefLink,
  getEntityRelations,
  useEntityRefLink,
} from '@backstage/plugin-catalog-react';
import { titleOf } from './templates';

/**
 * One template on the Create page (DX-650), drawn as a request card on Access
 * is. The card itself opens the template's form; below it, the team that owns
 * the template and its catalog entry are links of their own (AC-4).
 */

const useStyles = makeStyles(theme => ({
  card: {
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 8,
    border: '1px solid var(--ui-border)',
    background: 'var(--ui-bg-elevated)',
    color: 'var(--ui-text)',
    '&:hover': { borderColor: 'var(--ui-primary)' },
  },
  choose: {
    display: 'flex',
    gap: theme.spacing(1.5),
    alignItems: 'flex-start',
    width: '100%',
    padding: theme.spacing(1.75, 2, 0.75),
    border: 0,
    borderRadius: 8,
    background: 'transparent',
    color: 'inherit',
    font: 'inherit',
    textAlign: 'left',
    cursor: 'pointer',
    '&:focus-visible': {
      outline: '2px solid var(--ui-primary)',
      outlineOffset: 1,
    },
  },
  icon: {
    flexShrink: 0,
    display: 'grid',
    placeItems: 'center',
    width: 34,
    height: 34,
    borderRadius: 8,
    border: '1px solid var(--ui-border)',
    color: 'var(--ui-text-muted)',
  },
  text: { display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 },
  title: { fontWeight: 600, fontSize: '0.9375rem' },
  what: {
    fontSize: '0.8125rem',
    color: 'var(--ui-text-muted)',
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  meta: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    // Under the text, not under the icon: 2 (padding) + 34 (icon) + 1.5 (gap).
    padding: theme.spacing(0, 2, 1.75, 2 + 4.25 + 1.5),
    fontSize: '0.8125rem',
    color: 'var(--ui-text-muted)',
  },
}));

/** An icon by what the template creates, as the catalog's `spec.type` says. */
const ICONS: Record<string, ComponentType<SvgIconProps>> = {
  service: DnsIcon,
  website: WebIcon,
  library: LibraryIcon,
  documentation: DescriptionIcon,
};

type Props = {
  template: Entity;
  onChoose: () => void;
};

export const TemplateCard = ({ template, onChoose }: Props) => {
  const classes = useStyles();
  const entityLink = useEntityRefLink();
  const [owner] = getEntityRelations(template, RELATION_OWNED_BY);
  const type =
    typeof template.spec?.type === 'string' ? template.spec.type : undefined;
  const Icon = (type && ICONS[type]) || CategoryIcon;

  return (
    <article className={classes.card} aria-label={titleOf(template)}>
      <button type="button" className={classes.choose} onClick={onChoose}>
        <span className={classes.icon} aria-hidden>
          <Icon fontSize="small" />
        </span>
        <span className={classes.text}>
          <span className={classes.title}>{titleOf(template)}</span>
          {template.metadata.description && (
            <span className={classes.what}>
              {template.metadata.description}
            </span>
          )}
        </span>
      </button>
      <div className={classes.meta}>
        <PeopleIcon fontSize="inherit" aria-hidden />
        {owner ? (
          <EntityRefLink entityRef={owner} hideIcon disableTooltip />
        ) : (
          <span>No owner</span>
        )}
        {type && <span>· {type}</span>}
        <span aria-hidden>·</span>
        <Link to={entityLink(template)}>Details</Link>
      </div>
    </article>
  );
};
